/**
 * Web-enriched career copy — read layer. PRESENTATION ONLY.
 *
 * Source of truth: `public.content_enrichment` (entity_type = 'career'),
 * written only by the `enrich-content` edge function of the Evidence
 * Enrichment Framework. Nothing here is scored, and the governed registry
 * sections are never modified — enrichment is overlaid on top of the display
 * copy at read time, and only when it was graded `confident` and promoted.
 */
import { supabase } from "@/integrations/supabase/client";
import type { CareerIntel } from "@/lib/careerIntel";
import { fixArticles, tidyChips, tidyList } from "@/lib/careerText";

export interface EnrichmentSource {
  url: string;
  title?: string | null;
  domain?: string | null;
  identity?: string | null;
  trust_tier?: string | null;
  trust_weight?: number | null;
  fetched_at?: string | null;
  /** Retained Firecrawl text, so citations can be read in context. */
  excerpt?: string | null;
}

/** A contradiction found across sources on one claim axis. */
export interface EnrichmentConflict {
  axis: string;
  claims: string[];
  sources: string[];
}

export type EnrichmentFreshness = "fresh" | "ageing" | "stale" | "unknown";


export interface EnrichmentField {
  value: string | string[];
  sources: string[];
  /** The exact passage in each cited source that supports this field. */
  evidence?: Array<{ url: string; quote: string }>;
}

export const CAREER_ENRICHMENT_FIELDS = [
  "brief",
  "day_in_life",
  "career_story",
  "human_advantage",
  "future_outlook",
  "responsibilities",
  "must_have",
  "nice_to_have",
  "tools",
  "employers",
  "progression",
  "adjacent_roles",
  "entry_routes",
  "certifications",
  "emerging_trends",
] as const;

export type CareerEnrichmentFieldKey = typeof CAREER_ENRICHMENT_FIELDS[number];

export type EnrichmentPayload = Partial<Record<CareerEnrichmentFieldKey, EnrichmentField>>;

export type EnrichmentGrade = "confident" | "medium" | "weak";
export type EnrichmentStatus =
  | "queued" | "running" | "completed" | "failed" | "rejected" | "blocked" | "needs_review";
export type EnrichmentReviewStatus = "pending" | "approved" | "rejected" | "blocked";

export interface CareerEnrichment {
  entity_type: string;
  entity_id: string;
  /** Alias kept so existing career call-sites read naturally. */
  ci_id: string;
  canonical_name: string;
  status: EnrichmentStatus;
  grade: EnrichmentGrade;
  promoted: boolean;
  payload: EnrichmentPayload;
  sources: EnrichmentSource[];
  confidence: number;
  evidence_score: number;
  domain_count: number;
  supported_fields: number;
  /** Immutable version number of this enrichment (v1, v2, v3…). */
  version: number;
  /** Weighted authority of the sources, 0–1. */
  trust_score: number;
  /** Share of returned fields traceable to their cited sources, 0–1. */
  coverage: number;
  conflicts: EnrichmentConflict[];
  freshness: EnrichmentFreshness;
  model: string | null;
  prompt_version: string | null;
  hash: string | null;
  review_status: EnrichmentReviewStatus;
  reviewer: string | null;
  /** Free-text reviewer notes. Part of the exported governance record. */
  review_notes: string | null;
  stale: boolean;
  runtime_ms: number | null;
  cost: Record<string, unknown>;
  error: string | null;
  fetched_at: string | null;
  promoted_at: string | null;
  regraded_at: string | null;
  reviewed_at: string | null;
}

export const ENRICHMENT_COLUMNS =
  "entity_type, entity_id, entity_name, status, grade, promoted, payload, sources, confidence, " +
  "evidence_score, domain_count, supported_fields, version, trust_score, coverage, conflicts, " +
  "freshness, model, prompt_version, hash, review_status, reviewer, stale, runtime_ms, cost, " +
  "error, fetch_time, promotion_time, regraded_at, reviewed_at, review_notes";

export function normaliseEnrichment(row: any): CareerEnrichment {
  return {
    entity_type: row?.entity_type ?? "career",
    entity_id: row?.entity_id ?? "",
    ci_id: row?.entity_id ?? "",
    canonical_name: row?.entity_name ?? "",
    status: (row?.status ?? "queued") as EnrichmentStatus,
    grade: (row?.grade ?? "weak") as EnrichmentGrade,
    promoted: Boolean(row?.promoted),
    payload: (row?.payload ?? {}) as EnrichmentPayload,
    sources: Array.isArray(row?.sources) ? (row.sources as EnrichmentSource[]) : [],
    confidence: Number(row?.confidence ?? 0),
    evidence_score: Number(row?.evidence_score ?? 0),
    domain_count: Number(row?.domain_count ?? 0),
    supported_fields: Number(row?.supported_fields ?? 0),
    version: Number(row?.version ?? 0),
    trust_score: Number(row?.trust_score ?? 0),
    coverage: Number(row?.coverage ?? 0),
    conflicts: Array.isArray(row?.conflicts) ? (row.conflicts as EnrichmentConflict[]) : [],
    freshness: (row?.freshness ?? "unknown") as EnrichmentFreshness,
    model: row?.model ?? null,
    prompt_version: row?.prompt_version ?? null,
    hash: row?.hash ?? null,
    review_status: (row?.review_status ?? "pending") as EnrichmentReviewStatus,
    reviewer: row?.reviewer ?? null,
    review_notes: row?.review_notes ?? null,
    stale: Boolean(row?.stale),
    runtime_ms: row?.runtime_ms ?? null,
    cost: (row?.cost ?? {}) as Record<string, unknown>,
    error: row?.error ?? null,
    fetched_at: row?.fetch_time ?? null,
    promoted_at: row?.promotion_time ?? null,
    regraded_at: row?.regraded_at ?? null,
    reviewed_at: row?.reviewed_at ?? null,
  };
}


/**
 * The enrichment shown to a learner: promoted only. RLS already enforces this
 * for non-admins; the filter keeps the behaviour identical for admins.
 */
export async function loadPromotedEnrichment(ciId: string): Promise<CareerEnrichment | null> {
  const key = (ciId || "").trim();
  if (!key) return null;
  const { data } = await (supabase as any)
    .from("content_enrichment")
    .select(ENRICHMENT_COLUMNS)
    .eq("entity_type", "career")
    .eq("entity_id", key)
    .eq("promoted", true)
    .eq("status", "completed")
    .maybeSingle();
  return data ? normaliseEnrichment(data) : null;
}

/* ------------------------------------------------------------------ */
/* Overlay                                                             */
/* ------------------------------------------------------------------ */

const asText = (f: EnrichmentField | undefined): string | null =>
  f && typeof f.value === "string" && f.value.trim() ? f.value.trim() : null;

const asList = (f: EnrichmentField | undefined): string[] =>
  f && Array.isArray(f.value) ? tidyList(f.value.map(String).filter(Boolean)) : [];

/** Short-label lists (skills, tools, employers) — no fragment merging. */
const asChips = (f: EnrichmentField | undefined): string[] =>
  f && Array.isArray(f.value) ? tidyChips(f.value.map(String).filter(Boolean)) : [];

/** A blank intel record, so enrichment can render even with no v3.2 shard. */
function emptyIntel(name: string): CareerIntel {
  return {
    wk: null, name, aliases: [], family: null, cluster: null, level: null,
    iscoGroup: null, brief: null, responsibilities: [], mustHave: [],
    niceToHave: [], futureSkills: [], entry: null, subjects: [], pathways: [],
    salaryStart: null, salaryMid: null, salarySource: null,
    demand: { "2026": null, "2030": null, "2035": null, "2040": null },
    demandBasis: null, growthPct: null, remote: null, workLifeBalance: null,
    jobSatisfaction: null, freelance: null, entrepreneurship: null,
    automation: { exposure: null, tier: null, effect: null, horizon: null, note: null },
    humanAdvantage: null, aiResilience: null, futureReadiness: null,
    interests: [], strengths: [], archetypeMix: null, motivators: [], values: [],
    learningStyle: null, workingStyle: null, environment: null,
    adjacent: [], stretch: [], employers: [], dayInLife: null,
    socialImpact: null, environmentalImpact: null, evidence: null,
    confidence: null, provenance: null, lastUpdated: null,
  };
}

/**
 * Overlay researched copy onto the intelligence record.
 *
 * Enrichment wins for the fields it supports — for these roles the registry
 * text is a prior, and the researched text is evidence. Fields it does not
 * support are left exactly as they were. Salary, demand and automation are
 * never touched: those belong to `career_market_intelligence`.
 */
export function applyEnrichment(
  intel: CareerIntel | null,
  enrichment: CareerEnrichment | null,
  name: string,
): CareerIntel | null {
  if (!enrichment) return intel;
  const p = enrichment.payload;
  if (!p || !Object.keys(p).length) return intel;

  const base = intel ?? emptyIntel(name);
  const merged: CareerIntel = { ...base };

  const brief = asText(p.brief);
  if (brief) merged.brief = fixArticles(brief);
  const day = asText(p.day_in_life);
  if (day) merged.dayInLife = day;
  const human = asText(p.human_advantage);
  if (human) merged.humanAdvantage = human;

  const resp = asList(p.responsibilities);
  if (resp.length) merged.responsibilities = resp;
  const must = asChips(p.must_have);
  if (must.length) merged.mustHave = must;
  const nice = asChips(p.nice_to_have);
  const tools = asChips(p.tools);
  if (nice.length || tools.length) {
    merged.niceToHave = Array.from(new Set([...nice, ...tools, ...(nice.length ? [] : base.niceToHave)]));
  }
  const trends = asChips(p.emerging_trends);
  if (trends.length) merged.futureSkills = Array.from(new Set([...trends, ...base.futureSkills]));
  const employers = asChips(p.employers);
  if (employers.length) merged.employers = employers;
  const progression = asChips(p.progression);
  if (progression.length) merged.stretch = progression;
  const adjacent = asChips(p.adjacent_roles);
  if (adjacent.length) merged.adjacent = adjacent;
  const entry = asList(p.entry_routes);
  const certs = asChips(p.certifications);
  if (entry.length || certs.length) {
    merged.pathways = [...entry, ...certs.map((c) => `Certification: ${c}`)];
    if (!merged.entry && entry.length) merged.entry = entry[0];
  }

  return merged;
}

/** Which display fields on this page came from the web, in reading order. */
export const ENRICHED_FIELD_LABELS: Record<CareerEnrichmentFieldKey, string> = {
  brief: "What this work is",
  day_in_life: "A day in this work",
  career_story: "How people get here",
  human_advantage: "Your human edge",
  future_outlook: "Where this is heading",
  responsibilities: "What you'd be doing",
  must_have: "Non-negotiable skills",
  nice_to_have: "Helpful skills",
  tools: "Tools and platforms",
  employers: "Who hires for this",
  progression: "Where it leads",
  adjacent_roles: "Moves from here",
  entry_routes: "Ways in",
  certifications: "Credentials that count",
  emerging_trends: "What's changing",
};
